// @figma file=pkCGby54zXzTsccXvHqzO6 node=tbd name="FieldArray" set="mytaible-shadcn-studio" verified=tbd

import { Plus, X } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../atoms/button';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FieldArrayProps {
  /** Optional label rendered above the container */
  label?: string;
  /** Optional description rendered below the label in muted text */
  description?: string;
  /** Label for the Add button (default: "Add") */
  addLabel?: string;
  /** Called when the Add button is clicked */
  onAdd?: () => void;
  /** Current number of items — used to check against maxItems */
  itemCount?: number;
  /** When itemCount >= maxItems the Add button is hidden */
  maxItems?: number;
  disabled?: boolean;
  /** Validation error message rendered below the Add button */
  error?: string;
  className?: string;
  children?: React.ReactNode;
}

export interface FieldArrayItemProps {
  /** Called when the remove (X) button is clicked */
  onRemove?: () => void;
  disabled?: boolean;
  /** Optional drag handle element rendered on the far left of the row */
  dragHandle?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
}

// ─── FieldArrayItem ───────────────────────────────────────────────────────────

function FieldArrayItem({
  onRemove,
  disabled = false,
  dragHandle,
  className,
  children,
}: FieldArrayItemProps) {
  return (
    <div className={cn('flex items-center gap-3 px-3 py-3', className)}>
      {dragHandle && <div className="shrink-0">{dragHandle}</div>}

      <div className="min-w-0 flex-1">{children}</div>

      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          aria-label="Remove item"
          className={cn(
            'shrink-0 rounded p-0.5 text-muted-foreground transition-colors',
            'hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            disabled && 'pointer-events-none opacity-40',
          )}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
FieldArrayItem.displayName = 'FieldArrayItem';

// ─── FieldArray ───────────────────────────────────────────────────────────────

function FieldArray({
  label,
  description,
  addLabel = 'Add',
  onAdd,
  itemCount,
  maxItems,
  disabled = false,
  error,
  className,
  children,
}: FieldArrayProps) {
  const hasChildren = React.Children.count(children) > 0;
  const isAtMax = maxItems !== undefined && itemCount !== undefined && itemCount >= maxItems;
  const showAddButton = !isAtMax;

  return (
    <div className={cn('flex flex-col', className)}>
      {/* Label */}
      {label && <span className="mb-1 text-sm font-medium text-foreground">{label}</span>}

      {/* Description */}
      {description && <p className="mb-2 text-sm text-muted-foreground">{description}</p>}

      {/* Items container */}
      <div className="divide-y divide-border rounded-md border border-border">
        {hasChildren ? (
          children
        ) : (
          <p className="px-3 py-3 text-sm text-muted-foreground">No items added yet.</p>
        )}
      </div>

      {/* Add button */}
      {showAddButton && (
        <Button
          type="button"
          appearance="ghost"
          size="sm"
          onClick={onAdd}
          disabled={disabled}
          className="mt-2 self-start"
        >
          <Plus aria-hidden="true" />
          {addLabel}
        </Button>
      )}

      {/* Error */}
      {error && (
        <p className="mt-1 text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
FieldArray.displayName = 'FieldArray';

// ─── Exports ──────────────────────────────────────────────────────────────────

export { FieldArray, FieldArrayItem };
